'use client';

import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: string;
  name: string;
  role: string;
  image: string;
  alt: string;
  rating: number;
  comment: string;
}

interface TestimonialsCarouselProps {
  className?: string;
}

const TestimonialsCarousel = ({ className = '' }: TestimonialsCarouselProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
    {
      id: '1',
      name: 'Sumit Ghosh',
      role: 'Regular Customer',
      image: '/assets/people/1.jpeg',
      alt: 'Professional woman with long brown hair smiling warmly in casual blue sweater',
      rating: 5,
      comment:
        'Absolutely amazing food! The delivery was super fast and everything arrived hot and fresh. The flavors are authentic and portions are generous. Freshlymade has become my go-to for weeknight dinners.',
    },
    {
      id: '2',
      name: 'Sumit Tomar',
      role: 'Food Enthusiast',
      image: '/assets/people/2.jpeg',
      alt: 'Young Asian man with short black hair in white shirt smiling confidently outdoors',
      rating: 5,
      comment: 
        'Best food delivery service in town! The quality is consistently excellent and the prices are very reasonable. I love how they use fresh ingredients and you can really taste the difference. Highly recommended!',
    },
    {
      id: '3',
      name: 'Sonu Singh',
      role: 'Business Professional',
      image: '/assets/people/3.jpeg',
      alt: 'Hispanic woman with curly dark hair in professional attire smiling brightly',
      rating: 5,
      comment:
        'As someone who works long hours, Freshlymade is a lifesaver! The app is easy to use, delivery is always on time, and the food quality never disappoints. Their customer service is also top-notch.',
    },
  ];

  const handlePrevious = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index: number) => {
    if (!isHydrated) return;
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className={`bg-background py-12 lg:py-20 ${className}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="mb-4 font-heading text-3xl font-bold text-foreground opacity-0 animate-slideUp lg:text-4xl">
            What Our Customers Say
          </h2>
          <p className="mx-auto max-w-2xl text-base text-muted-foreground opacity-0 animate-slideUp animate-delay-100 lg:text-lg">
            Real reviews from real food lovers who trust Freshlymade
          </p>
        </div>

        <div className="relative mx-auto max-w-4xl">
          {/* Testimonial Card */}
          <div
            key={currentIndex}
            className="rounded-2xl border border-border/50 bg-card p-8 shadow-xl opacity-0 animate-fadeInUp lg:p-12"
          >
            <div className="mb-8 flex flex-col items-center text-center sm:flex-row sm:text-left">
              <div className="mb-4 h-20 w-20 flex-shrink-0 overflow-hidden rounded-full sm:mb-0 sm:mr-6">
                <AppImage
                  key={currentTestimonial.id}
                  src={currentTestimonial.image}
                  alt={currentTestimonial.alt}
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="flex-1">
                <h3 className="mb-1 font-heading text-xl font-semibold text-foreground">
                  {currentTestimonial.name}
                </h3>
                <p className="text-caption text-muted-foreground">{currentTestimonial.role}</p>
                <div className="mt-2 flex justify-center sm:justify-start">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Icon
                      key={index}
                      name="StarIcon"
                      variant="solid"
                      size={20}
                      className={index < currentTestimonial.rating ? 'text-accent' : 'text-muted'}
                    />
                  ))}
                </div>
              </div>
            </div>

            <blockquote className="text-center text-base leading-relaxed text-foreground sm:text-left lg:text-lg">
              "{currentTestimonial.comment}"
            </blockquote>
          </div>

          {/* Navigation Controls */}
          <div className="mt-8 flex items-center justify-center space-x-4">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={!isHydrated}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl active:scale-95 focus-ring disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Previous testimonial"
            >
              <Icon name="ChevronLeftIcon" size={24} />
            </button>

            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDotClick(index)}
                  disabled={!isHydrated}
                  className={`h-3 w-3 rounded-full transition-smooth focus-ring disabled:cursor-not-allowed ${
                    index === currentIndex ? 'bg-primary' : 'bg-muted'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={handleNext}
              disabled={!isHydrated}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl active:scale-95 focus-ring disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Next testimonial"
            >
              <Icon name="ChevronRightIcon" size={24} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;